/**
 * The frame around the sign-in and sign-up pages (#371).
 *
 * Deliberately NOT `AppShell`: that shell carries the learning navigation and
 * the account control, and a person who is not signed in has neither a
 * destination to navigate to nor an account to control. It does keep the two
 * things every screen owes: a skip link first in the tab order, and exactly one
 * `<main>`; the page inside supplies the one `<h1>`.
 *
 * It makes no API request. These pages render when there is no session, and a
 * layout that fetched anything would answer 401 and bounce the person to the
 * page they are already on.
 */

import { Logo } from './logo.js';
import { Outlet } from 'react-router';
import { useMessages, useProfileLocale } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';
import { LanguageSwitch } from './language-switch.js';
import { useMorphCommit } from './page-morph.js';

const messages = defineMessages({
  en: { skip: 'Skip to main content' },
  he: { skip: 'דילוג לתוכן הראשי' },
});

const stepMessages = defineMessages({
  en: { label: 'Signup steps', account: 'Account', consent: 'Consent', moodle: 'Moodle', courses: 'Courses' },
  he: { label: 'שלבי ההרשמה', account: 'חשבון', consent: 'הסכמה', moodle: 'מודל', courses: 'קורסים' },
});

/**
 * The stage the sign-in, sign-up and consent screens stand on (RegInvitePc.dc.html): a dark hero panel with three
 * round course-colour circles and a tile holding one glyph, beside a card. The hero is decorative and is dropped on a
 * phone, where the card is the page (RegInvite.dc.html).
 */
export function AuthStage({
  glyph,
  compact = false,
  heroOnPhone = false,
  children,
}: {
  /** What the hero tile holds: a text glyph, an SVG (`AuthHeroIcon`) or `AuthHeroDots`. Sized by CSS. */
  readonly glyph: React.ReactNode;
  /** Keep the hero on a phone (the welcome step, RegWelcome); every other page drops it there. */
  readonly heroOnPhone?: boolean;
  /** A one-step card (sign-in): no tall frame to fill. */
  readonly compact?: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="auth-stage" data-hero-on-phone={heroOnPhone ? 'true' : undefined}>
      <div className="auth-hero" aria-hidden="true">
        <span className="auth-hero-dot auth-hero-dot-teal" />
        <span className="auth-hero-dot auth-hero-dot-ochre" />
        <span className="auth-hero-dot auth-hero-dot-plum" />
        <span className="auth-hero-centre">
          <span className="auth-hero-tile">{glyph}</span>
        </span>
      </div>
      <section className="auth-card" data-compact={compact ? "true" : undefined}>{children}</section>
    </div>
  );
}

/** Three course-colour dots: the welcome step's tile (RegWelcome). */
export function AuthHeroDots(): React.JSX.Element {
  return (
    <span className="auth-hero-dots" dir="ltr">
      <span data-course="teal" />
      <span data-course="ochre" />
      <span data-course="plum" />
    </span>
  );
}

/** An outline SVG for the hero tile; `children` are the board's paths (24 x 24 box). */
export function AuthHeroIcon({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <svg className="auth-hero-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

/**
 * The steps of signing up, as discrete steps and never a proportion: Account, Consent, Moodle, Courses (#648: the
 * last two are `/welcome`, entered after the consent gate's first save).
 */
export function SignupSteps({ current }: { readonly current: 'account' | 'consent' | 'moodle' | 'courses' }): React.JSX.Element {
  const t = useMessages(stepMessages);
  const steps = [
    { key: 'account', name: t.account },
    { key: 'consent', name: t.consent },
    { key: 'moodle', name: t.moodle },
    { key: 'courses', name: t.courses },
  ] as const;
  const at = steps.findIndex((step) => step.key === current);
  return (
    <ol className="auth-steps" aria-label={t.label}>
      {steps.map((step, index) => (
        <li key={step.key} aria-current={index === at ? 'step' : undefined} data-reached={index <= at ? 'true' : 'false'}>
          <span className="auth-step-bar" aria-hidden="true" />
          <span className="auth-step-name">{step.name}</span>
        </li>
      ))}
    </ol>
  );
}

export function AuthLayout(): React.JSX.Element {
  const t = useMessages(messages);
  useProfileLocale(false);
  useMorphCommit();
  return (
    <div className="app-shell auth-shell">
      <a className="skip-link" href="#main">
        {t.skip}
      </a>

      <header className="app-header">
        <Logo />
        <LanguageSwitch />
      </header>

      <main className="app-main" id="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
